import { Offscreen, Step, Stepper } from '@deque/cauldron-react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { type TransInterpolation } from '~/common/types';

import type { StepMap } from '~/app/types';
import useMediaQuery from '~/common/hooks/useMediaQuery';
import getStepStatus, {
  getCapitalizedStepStatus
} from '~/common/utils/get-step-status';
import styles from './TemplateStepper.css';

export interface TemplateStepper {
  currentStep: number;
  steps: StepMap[];
}

interface StepContentProps {
  currentStep: number;
  index: number;
  header: string;
  narrow: boolean;
}
const StepContent = ({
  currentStep,
  index,
  header,
  narrow
}: StepContentProps) => {
  const { t } = useTranslation();

  const stepStatus = getStepStatus(currentStep, index);
  const stepStatusCapitalized = getCapitalizedStepStatus(t, stepStatus);
  const offscreenStepStatus =
    stepStatus === 'complete'
      ? t('Completed step')
      : t('{{stepStatusCapitalized}} step', { stepStatusCapitalized });

  if (!narrow) {
    return (
      <Step status={stepStatus} key={header}>
        {header}
        <Offscreen>{offscreenStepStatus}</Offscreen>
      </Step>
    );
  }

  const tooltip = (
    <Trans>
      <div>
        Step {{ i: index + 1 } as TransInterpolation}:{' '}
        {{ header } as TransInterpolation}
      </div>
      <div>Status: {{ stepStatusCapitalized } as TransInterpolation}</div>
    </Trans>
  );

  return (
    <Step
      status={stepStatus}
      key={header}
      tooltip={tooltip}
      tooltipText={`${header} ${stepStatus}`}
    >
      <Offscreen>{`${header} ${offscreenStepStatus}`}</Offscreen>
    </Step>
  );
};

const TemplateStepper = ({ currentStep, steps }: TemplateStepper) => {
  const narrow = useMediaQuery('(max-width: 37.5rem)');

  return (
    <Stepper className={narrow ? styles.narrowStepper : styles.stepper}>
      {steps.map(({ header }, index) => (
        <StepContent
          key={header}
          currentStep={currentStep}
          index={index}
          header={header}
          narrow={narrow}
        />
      ))}
    </Stepper>
  );
};

export default TemplateStepper;
